import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withFetch,
} from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '@environments';
import { routes } from './app.routes';

/**
 * Backend must respond successfully before the app bootstraps; otherwise bootstrap fails.
 *
 * Uses HttpBackend (via a dedicated HttpClient) so the call does not go through MsalInterceptor.
 * Otherwise requests under BACKEND_API_BASE_URL match the protected resource map and MSAL tries
 * to attach a token before login, which can block or fail the health check on initial load.
 */
export function backendHealthCheckInitializer(httpBackend: HttpBackend) {
  return async () => {
    const base = environment.BACKEND_API_BASE_URL?.trim();
    if (!base) {
      const msg = 'BACKEND_API_BASE_URL is not set; cannot run backend health check.';
      console.error('AppConfig:', msg);
      return await Promise.reject(new Error(msg));
    }

    const http = new HttpClient(httpBackend);
    const url = `${base}/health`;
    console.info('AppConfig: Backend health check →', url);
    try {
      await firstValueFrom(http.get(url, { responseType: 'json' }));
      console.info('AppConfig: Backend health check succeeded');
    } catch (err) {
      console.error('AppConfig: Backend health check failed', err);
      return await Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
  };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.AAD_CLIENT_ID,
      authority: environment.AAD_AUTHORITY,
      redirectUri: environment.AAD_REDIRECT_URI,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
      [environment.BACKEND_API_BASE_URL, [environment.AAD_USER_API_SCOPE]],
    ]),
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAnimationsAsync(),
    provideAppInitializer(() => backendHealthCheckInitializer(inject(HttpBackend))()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
  ],
};